/* Every derived number in the app is computed here and nowhere else.
   The dashboard and the monthly report must agree, and the only way to
   guarantee that is for both to call the same function.

   Three rules here deliberately differ from prototype/contract-ops.jsx.
   Each is marked RULE and matches CLAUDE.md, which reads as the considered
   intent where the prototype reads as an oversight. Decisions recorded in
   PHASE_1_PLAN.md. */

import { T, STATUS } from "../theme/tokens.js";
import { today, addDays, daysFrom } from "./format.js";

export const DEFAULT_TERMS_DAYS = 60;

/* The default high risk threshold, right for a 5x5 register like WAPA.
   It is a default and not a rule, because it cannot be one number for every
   contract. VIDE scores probability and impact 1 to 3, with an occasional 4
   for Critical, so its highest possible score is 12 and this threshold would
   flag nothing at all, including the 74 risks it scores 9, which are that
   register's High against High. Each contract carries its own threshold in
   contracts.high_risk_threshold. */
export const DEFAULT_HIGH_RISK_THRESHOLD = 15;
export const NEARING_TERMS_DAYS = 15;
export const PERMIT_AGING_DAYS = 30;
export const BUDGET_AMBER = 0.75;
export const BUDGET_RED = 0.9;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* ---------- deliverables ---------- */

/* status is the confirmed status, the one the program stands behind.
   reported_status is what the vendor said, which is often nothing: 69 of
   WAPA's 188 rows report "Status Not Reported" yet carry a real current
   status. Every rollup and every report reads the confirmed one. */
export const isComplete = (d) => d.status === "Complete";

/* RULE 1. The effective due date is the contractual date, falling back to
   the schedule of record when no contractual date exists. The prototype
   only ever read dueDate, so a deliverable carrying only a schedule date
   could never go overdue. */
export const effectiveDueDate = (d) => d.due_date || d.schedule_date || null;

export const isOverdue = (d, ref = today()) => {
  const due = effectiveDueDate(d);
  return !isComplete(d) && !!due && due < ref;
};

export const isOverdueContractDeliverable = (d, ref = today()) =>
  isOverdue(d, ref) && d.is_contract_deliverable === true;

/* Variance between the contractual date and the schedule of record. Positive
   means the schedule has drifted later than the contract allows. */
export const scheduleVarianceDays = (d) =>
  d.due_date && d.schedule_date
    ? daysFrom(d.due_date, d.schedule_date)
    : null;

/* The four states a deliverable is drawn in.
 *
 * Overdue replaces the state underneath it rather than sitting beside it, so
 * the four always add up to the total and nothing is counted twice. That rule
 * lived in three places on the dashboard, written out longhand each time, and
 * one of those copies passed the array index into isOverdue as the date to
 * compare against. The bar drew twelve overdue deliverables and opened none of
 * them. One definition now, and it is tested.
 */
export const DELIVERABLE_STATES = ["Complete", "In progress", "Not started", "Overdue"];

export function deliverableState(d, ref = today()) {
  if (isOverdue(d, ref)) return "Overdue";
  if (isComplete(d)) return "Complete";
  return d.status === "In Progress" ? "In progress" : "Not started";
}

/* The same four, as the rows in each. Every chart that shows a state shows
   these, and every click opens exactly what was counted. */
export function groupByState(deliverables = [], ref = today()) {
  const out = Object.fromEntries(DELIVERABLE_STATES.map((k) => [k, []]));
  for (const d of deliverables) out[deliverableState(d, ref)].push(d);
  return out;
}

/* Replicates the owner's Excel Dashboard tab. Rows per vendor plus a
   program total. */
export function vendorRollup(deliverables = [], ref = today()) {
  /* Every cell in this table carries the deliverables it counted, so a click
     on the 1 under Overdue CD opens that one deliverable rather than leaving
     somebody to find it. */
  const BUCKETS = ["total", "complete", "inProgress", "notStarted",
                   "overdue", "overdueContractDeliverables"];
  const blank = () => ({
    total: 0, complete: 0, inProgress: 0, notStarted: 0,
    overdue: 0, overdueContractDeliverables: 0,
    records: Object.fromEntries(BUCKETS.map((b) => [b, []])),
  });
  const byVendor = new Map();

  const put = (r, bucket, d) => { r[bucket] += 1; r.records[bucket].push(d); };

  for (const d of deliverables) {
    const key = d.vendor || "Unassigned";
    if (!byVendor.has(key)) byVendor.set(key, blank());
    const r = byVendor.get(key);
    put(r, "total", d);
    if (isComplete(d)) put(r, "complete", d);
    else if (d.status === "In Progress") put(r, "inProgress", d);
    else put(r, "notStarted", d);
    if (isOverdue(d, ref)) {
      put(r, "overdue", d);
      if (d.is_contract_deliverable === true) put(r, "overdueContractDeliverables", d);
    }
  }

  const rows = [...byVendor.entries()]
    .map(([vendor, r]) => ({ vendor, ...r, percentComplete: pct(r.complete, r.total) }))
    .sort((a, b) => a.vendor.localeCompare(b.vendor));

  const total = rows.reduce((a, r) => {
    for (const b of BUCKETS) {
      a[b] += r[b];
      a.records[b].push(...r.records[b]);
    }
    return a;
  }, blank());

  return { rows, total: { ...total, percentComplete: pct(total.complete, total.total) } };
}

const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : null);

/* ---------- invoices ---------- */

/* RULE 3. Terms fall back in order: the invoice's own terms, then the
   vendor's terms, then the contract's payment terms, then 60 days. The
   prototype jumped straight from the invoice to a hard 60, so a Net 45
   vendor aged as though it were Net 60. */
export function termsFor(invoice, { vendorTerms, contractTerms } = {}) {
  const candidates = [invoice?.terms, vendorTerms, contractTerms, DEFAULT_TERMS_DAYS];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return DEFAULT_TERMS_DAYS;
}

export const AGING_BUCKETS = [
  "Within terms", "1-30 past", "31-60 past", "61-90 past", "90+ past",
];

/* Aging is measured against each invoice's own net terms, not calendar age
   and not a flat 30/60/90. */
export function invoiceAging(invoice, ctx = {}, ref = today()) {
  if (invoice.paid_date) {
    return { label: "Paid", color: T.ok, pastDays: 0, open: false, bucket: null };
  }
  if (!invoice.submitted) {
    return { label: "No date", color: T.soft, pastDays: 0, open: true, bucket: null };
  }

  const terms = termsFor(invoice, ctx);
  const dueOn = addDays(invoice.submitted, terms);
  const past = daysFrom(dueOn, ref);

  if (past <= 0) {
    const nearing = past >= -NEARING_TERMS_DAYS;
    return {
      label: nearing ? "Nearing terms" : "Within terms",
      color: nearing ? T.warn : T.steel,
      pastDays: 0, open: true, bucket: "Within terms", termsDays: terms, dueOn,
    };
  }

  const bucket =
    past <= 30 ? "1-30 past" :
    past <= 60 ? "31-60 past" :
    past <= 90 ? "61-90 past" : "90+ past";

  return {
    label: `${past}d past terms`, color: T.risk,
    pastDays: past, open: true, bucket, termsDays: terms, dueOn,
  };
}

/* Vendor terms are looked up by name because imported invoices carry vendor
   names as free text rather than as links. */
export const vendorTermsLookup = (vendors = []) => {
  const map = new Map();
  for (const v of vendors) {
    /* Vendor rows carry the name in `title`, the shared spine column every
       register table uses. `name` is accepted too so a plain object from a
       test or an import staging step works without ceremony. */
    const key = v.title ?? v.name;
    if (key) map.set(String(key).trim().toLowerCase(), v.terms_days);
  }
  return (name) => map.get(String(name || "").trim().toLowerCase());
};

export function invoiceSummary(invoices = [], { vendors = [], contractTerms } = {}, ref = today()) {
  const lookupTerms = vendorTermsLookup(vendors);
  const ctxFor = (i) => ({ vendorTerms: lookupTerms(i.vendor), contractTerms });

  const priced = invoices.map((i) => ({ invoice: i, aging: invoiceAging(i, ctxFor(i), ref) }));
  const open = priced.filter((p) => p.aging.open);
  const past = open.filter((p) => p.aging.pastDays > 0);

  const buckets = Object.fromEntries(AGING_BUCKETS.map((b) => [b, { dollars: 0, count: 0 }]));
  for (const p of open) {
    const b = p.aging.bucket || "Within terms";
    buckets[b].dollars += num(p.invoice.amount);
    buckets[b].count += 1;
  }

  const byVendor = new Map();
  for (const p of open) {
    const key = p.invoice.vendor || "Unassigned";
    if (!byVendor.has(key)) byVendor.set(key, { vendor: key, open: 0, past: 0, pastCount: 0 });
    const r = byVendor.get(key);
    r.open += num(p.invoice.amount);
    if (p.aging.pastDays > 0) {
      r.past += num(p.invoice.amount);
      r.pastCount += 1;
    }
  }

  const worst = [...past]
    .sort((a, b) => b.aging.pastDays - a.aging.pastDays)
    .slice(0, 5);

  /* The monthly deck groups aging as Over-60 and 0-60 rather than by the
     five screen buckets. */
  const over60 = past.filter((p) => p.aging.pastDays > 60);
  const zeroTo60 = open.filter((p) => p.aging.pastDays <= 60);

  return {
    priced, open, past, buckets, worst,
    totalOutstanding: open.reduce((s, p) => s + num(p.invoice.amount), 0),
    pastTermsDollars: past.reduce((s, p) => s + num(p.invoice.amount), 0),
    pastTermsCount: past.length,
    oldestPastDays: past.length ? Math.max(...past.map((p) => p.aging.pastDays)) : 0,
    byVendor: [...byVendor.values()].sort((a, b) => b.past - a.past),
    reportGroups: {
      over60: { count: over60.length, dollars: over60.reduce((s, p) => s + num(p.invoice.amount), 0), items: over60 },
      zeroTo60: { count: zeroTo60.length, dollars: zeroTo60.reduce((s, p) => s + num(p.invoice.amount), 0), items: zeroTo60 },
    },
  };
}

/* ---------- risks ---------- */

/* RULE 2. One threshold per contract, applied everywhere including the
   monthly report. The prototype's overview used 15 while its report builder
   used 12, so the deck flagged risks the dashboard did not. Both now read the
   same number, and that number comes from the contract rather than from a
   constant, because the two programs score on different scales.
   Registers imported from a 3x3 scale keep their own numbers and are
   displayed as they are. */
export const riskScore = (r) => (num(r.likelihood) || 1) * (num(r.impact) || 1);
export const isOpenRisk = (r) => r.status !== "Closed";

export const highRiskThreshold = (contract) => {
  const n = Number(contract?.high_risk_threshold);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_HIGH_RISK_THRESHOLD;
};

/* Not every register scores. The WAPA RAID log rates an entry High, Medium or
   Low and leaves the numbers blank, so scoring it would multiply two missing
   values into 1 and report a High risk as the mildest thing on the board. A
   risk that carries numbers is judged by its score against the contract's
   threshold, exactly as before. A risk that carries only a rating is judged by
   the rating. A risk that carries neither is not high, because nobody has said
   it is. */
const HIGH_RATINGS = new Set(["high", "very high", "critical", "severe", "red"]);

export const isScored = (r) => num(r.likelihood) > 0 && num(r.impact) > 0;
export const isRatedHigh = (r) =>
  HIGH_RATINGS.has(String(r.rating ?? "").trim().toLowerCase());

export const isHighRisk = (r, contract) =>
  isOpenRisk(r) &&
  (isScored(r) ? riskScore(r) >= highRiskThreshold(contract) : isRatedHigh(r));

export const highRisks = (risks = [], contract) =>
  risks.filter((r) => isHighRisk(r, contract));

/* ---------- budget ---------- */

export function budgetPosture(contract = {}, expenses = [], resources = []) {
  const laborCost = resources.reduce((s, r) => s + num(r.rate) * num(r.hours_used), 0);
  const expenseSum = expenses.reduce((s, e) => s + num(e.amount), 0);
  /* WOB's cost from Workday is the truth when it is set: it is the firm's own
     cost against its award, which the expenses register may not hold. Fall
     back to the register sum only when no snapshot exists, so a program with
     no cost entered reads honestly rather than falsely zero. */
  const hasSnapshot = contract.cost_to_date !== null && contract.cost_to_date !== undefined;
  const spent = hasSnapshot ? num(contract.cost_to_date) : expenseSum;
  const spentSource = hasSnapshot ? "workday" : "register";
  const ceiling = num(contract.not_to_exceed) || num(contract.value) || 0;
  const ratio = ceiling ? spent / ceiling : 0;
  return {
    spent, laborCost, ceiling, spentSource, asOf: contract.financials_as_of || null,
    percentUsed: ceiling ? Math.round(ratio * 100) : null,
    level: !ceiling ? "none" : ratio >= BUDGET_RED ? "red" : ratio >= BUDGET_AMBER ? "amber" : "ok",
    /* The same amber and red as every state chip, so a budget in trouble and a
       deliverable in trouble look like the same kind of trouble. */
    color: !ceiling ? T.soft
      : ratio >= BUDGET_RED ? STATUS.overdue.ink
      : ratio >= BUDGET_AMBER ? STATUS.inProgress.ink
      : T.navy,
  };
}

/* ---------- forecast ---------- */

/* Where the money lands, not where it stands.
 *
 * The question a sponsor asks is not "how much have we spent", it is "will
 * this finish over budget and by how much". That is forecast at completion.
 * The honest version needs an earned-value curve this app does not keep, so
 * this is the straight-line estimate: if a quarter of the work has cost this
 * much, the whole will cost four times it. It is labelled an estimate, never
 * dressed up as formal EVM.
 *
 * Early percentages are unstable: at 2 percent complete a rounding error in
 * progress swings the forecast wildly, so below a floor it declines to guess
 * and says so. A sponsor prefers "too early to forecast" to a confident wrong
 * number.
 */
export const FORECAST_FLOOR_PCT = 8;

export function forecastAtCompletion({ ceiling = 0, spent = 0, percentComplete = null } = {}) {
  const pct = percentComplete;
  if (pct === null || pct === undefined || pct < FORECAST_FLOOR_PCT) {
    return { eac: null, vac: null, overrun: null, level: "unknown", basis: "too early to forecast" };
  }
  /* No cost recorded is not "finishing under budget", it is "we cannot see the
     spend". A program delivering work with zero cost on the books means the
     cost lives somewhere the app is not reading yet, so decline to forecast
     rather than report a false saving. */
  if (!(spent > 0)) {
    return { eac: null, vac: null, overrun: null, level: "unknown", basis: "no cost recorded" };
  }
  const eac = Math.round(spent / (pct / 100));
  if (!ceiling) {
    return { eac, vac: null, overrun: null, level: "none", basis: "no ceiling set" };
  }
  const vac = ceiling - eac;            // positive is under budget
  const ratio = eac / ceiling;
  return {
    eac, vac,
    overrun: vac < 0 ? -vac : 0,
    level: ratio >= 1.05 ? "red" : ratio > 1 ? "amber" : "ok",
    basis: "straight-line estimate",
  };
}

/* The gap that is the story.
 *
 * "We spent 60 percent" is a rearview number. "We spent 60 percent with 40
 * percent of the time left and 70 percent of the scope undelivered" is a
 * leading one: it says the burn and the delivery pace have come apart, weeks
 * before a milestone formally slips. Three measures on one scale, 0 to 100:
 * how much of the money is gone, how much of the clock is gone, how much of
 * the work is done.
 *
 * Two gaps come out of it. The burn gap is spend minus scope: positive means
 * money is running ahead of value, the early shape of an overrun. The schedule
 * gap is time minus scope: positive means the calendar is beating delivery.
 * The worse of the two sets the posture, because either one is a reason to
 * act. Below a floor of progress the gaps are noise, so it declines to judge.
 */
export const PACE_FLOOR_PCT = 5;
export const PACE_AMBER = 7;
export const PACE_RED = 15;

export function burnVsScope({ ceiling = 0, spent = 0, startDate = null, endDate = null,
                              percentComplete = null, asOf = today() } = {}) {
  const spentPct = ceiling ? Math.round((spent / ceiling) * 100) : null;
  const scopePct = percentComplete;

  let timePct = null;
  if (startDate && endDate) {
    const a = new Date(startDate).getTime();
    const b = new Date(endDate).getTime();
    const n = new Date(asOf).getTime();
    if (b > a) timePct = Math.max(0, Math.min(100, Math.round(((n - a) / (b - a)) * 100)));
  }

  const burnGap = (spentPct !== null && scopePct !== null) ? spentPct - scopePct : null;
  const scheduleGap = (timePct !== null && scopePct !== null) ? timePct - scopePct : null;

  /* The posture only means something once there is progress to compare the
     spend and the calendar against. */
  if (scopePct === null || scopePct < PACE_FLOOR_PCT || (burnGap === null && scheduleGap === null)) {
    return { spentPct, timePct, scopePct, burnGap, scheduleGap, level: "unknown" };
  }
  const worst = Math.max(burnGap ?? -Infinity, scheduleGap ?? -Infinity);
  const level = worst >= PACE_RED ? "red" : worst >= PACE_AMBER ? "amber" : "ok";
  return { spentPct, timePct, scopePct, burnGap, scheduleGap, level };
}

/* One posture for a whole program, scored the same way on the single-program
 * Overview and on the portfolio, so a leader comparing two cards is comparing
 * like with like. The research on executive dashboards is blunt about this:
 * the moment one project's "at risk" means something different from another's,
 * the rollup is noise. So the rule lives here, once.
 *
 * Rank is for sorting worst-first: 2 red, 1 amber, 0 green.
 */
export function programPosture({ budgetLevel = "none", forecastLevel = "unknown",
                                 overdueCDs = 0, highRisks = 0,
                                 paceLevel = "unknown", compliance = "none",
                                 modExposed = false } = {}) {
  const red = budgetLevel === "red" || forecastLevel === "red" || paceLevel === "red"
    || compliance === "red" || overdueCDs > 0 || highRisks > 2 || modExposed;
  const amber = budgetLevel === "amber" || forecastLevel === "amber" || paceLevel === "amber"
    || compliance === "amber" || highRisks > 0;
  if (red) return { label: "Needs attention", color: STATUS.overdue.ink, key: "red", rank: 2 };
  if (amber) return { label: "Watch", color: STATUS.inProgress.ink, key: "amber", rank: 1 };
  return { label: "On track", color: STATUS.complete.ink, key: "green", rank: 0 };
}

/* ---------- the team ---------- */

/* What leadership asks about the people: who is on this, how much of them we
   have, and how far through their hours they are. Two different numbers kept
   side by side. Allocation is entered and may be blank; utilization is derived
   from hours and is only meaningful where hours were planned. */
export function teamStats(resources = []) {
  const people = resources
    .filter((r) => r.title)
    .map((r) => {
      const planned = num(r.hours_planned);
      const used = num(r.hours_used);
      const alloc = r.allocation_pct === null || r.allocation_pct === undefined
        ? null : Math.max(0, Math.min(100, Math.round(num(r.allocation_pct))));
      return {
        id: r.id,
        name: r.title,
        role: r.role || null,
        allocation: alloc,
        planned, used,
        /* Over 100 is real and worth seeing, not clamped away: it means a
           person has burned more than was booked for them. */
        utilization: planned > 0 ? Math.round((used / planned) * 100) : null,
        laborCost: num(r.rate) * used,
      };
    })
    .sort((a, b) => (b.allocation ?? -1) - (a.allocation ?? -1)
      || a.name.localeCompare(b.name));

  const withAlloc = people.filter((p) => p.allocation !== null);
  return {
    people,
    headcount: people.length,
    /* The sum of committed shares, in whole-person equivalents. Three people
       at 50, 50 and 100 is 2.0 FTE on the project. */
    fte: withAlloc.length
      ? Math.round(withAlloc.reduce((s, p) => s + p.allocation, 0)) / 100
      : null,
    allocationKnown: withAlloc.length,
  };
}

/* Gross margin, the realization question: are we earning what we priced.
 *
 * From the WOB snapshot, revenue recognised against cost to date. It is not
 * derived from the app's rows, so it is only shown where both figures have
 * been entered, with the date they were taken. A margin number with no date is
 * the kind of figure a leader catches wrong once and never trusts again. */
export function grossMargin(contract = {}) {
  const revenue = contract.revenue_to_date;
  const cost = contract.cost_to_date;
  if (revenue === null || revenue === undefined || cost === null || cost === undefined) {
    return { pct: null, revenue: null, cost: null, asOf: null };
  }
  const rev = num(revenue), c = num(cost);
  return {
    pct: rev > 0 ? Math.round(((rev - c) / rev) * 1000) / 10 : null,
    revenue: rev, cost: c, profit: rev - c,
    asOf: contract.financials_as_of || null,
  };
}

/* ---------- permits and RFIs ---------- */

export function permitDaysOpen(p, ref = today()) {
  if (!p.submitted) return null;
  const start = new Date(p.submitted);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(p.closed_date || ref);
  return Math.max(0, Math.round((end - start) / 86400000));
}

/* Average days open covers the open items only. "How long are the ones we
   are still waiting on sitting" is the number that gets asked on a status
   call, and closed items would drag it toward history. */
export function permitKpis(permits = [], ref = today()) {
  const open = permits.filter((p) => p.status !== "Closed");
  const ages = open.map((p) => permitDaysOpen(p, ref)).filter((n) => n !== null);
  const aging = open.filter((p) => (permitDaysOpen(p, ref) ?? 0) > PERMIT_AGING_DAYS);
  return {
    total: permits.length,
    open: open.length,
    closed: permits.length - open.length,
    openOverThreshold: aging.length,
    averageDaysOpen: ages.length ? Math.round(ages.reduce((s, n) => s + n, 0) / ages.length) : null,
    /* The rows behind each figure, so a screen can open them without
       restating the rule for what counts as open. Two copies of that rule is
       how a card and the list it opens end up disagreeing. */
    records: {
      total: permits,
      open,
      closed: permits.filter((p) => p.status === "Closed"),
      openOverThreshold: aging,
      averageDaysOpen: open,
    },
  };
}

/* ---------- field trackers ---------- */

export function trackerTotals(rows = []) {
  const total = rows.reduce((s, r) => s + num(r.total), 0);
  const complete = rows.reduce((s, r) => s + num(r.complete), 0);
  const thisPeriod = rows.reduce((s, r) => s + num(r.this_period), 0);
  const outstanding = rows
    .filter((r) => num(r.complete) < num(r.total) && r.est_completion)
    .map((r) => r.est_completion)
    .sort();
  return {
    total, complete, thisPeriod,
    percentComplete: total ? Math.round((complete / total) * 1000) / 10 : 0,
    estCompletion: outstanding.length ? outstanding[outstanding.length - 1] : "Complete",
  };
}

/* ---------- forward look ---------- */

export function upcomingWindow({ milestones = [], deliverables = [] }, days = 30, ref = today()) {
  const horizon = addDays(ref, days);
  const items = [
    /* Each item keeps the row it came from, so the screen can open it. */
    ...milestones
      .filter((m) => m.status !== "Complete" && m.date)
      .map((m) => ({ title: m.title, date: m.date, kind: "milestone", row: m })),
    ...deliverables
      .filter((d) => !isComplete(d) && effectiveDueDate(d))
      .map((d) => ({ title: d.title, date: effectiveDueDate(d), kind: "deliverable",
                     vendor: d.vendor, row: d })),
  ];
  return items
    .filter((i) => i.date >= ref && i.date <= horizon)
    .sort((a, b) => a.date.localeCompare(b.date));
}

/* ---------- schedule ---------- */

/* Dates are plain YYYY-MM-DD strings everywhere in this file, so the timeline
   works in whole days counted from the epoch rather than in Date objects. No
   timezone can move a bar by a day. */
const dayIndex = (d) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d ?? ""));
  return m ? Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000) : null;
};

const monthKey = (d) => String(d).slice(0, 7);
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return `${MONTH_NAMES[+m - 1]} ${y.slice(2)}`;
};
const monthStart = (key) => `${key}-01`;
const monthEnd = (key) => {
  const [y, m] = key.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${key}-${String(last).padStart(2, "0")}`;
};
const nextMonth = (key) => {
  const [y, m] = key.split("-").map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
};

/* A bar per phase, however this contract records its phases.

   Where the schedule register holds stages, those are the phases and nothing
   is inferred. Where it does not, the phases are built from the deliverables,
   which is the ordinary case here: WAPA's 188 rows carry a Stage each and no
   formal stage rows exist, so the eight stages become eight bars running from
   the earliest to the latest date in each. A contract whose deliverables
   carry no stage falls back to one bar a month.

   A derived bar is marked as derived, because a bar the program drew and a
   bar the app inferred are not the same claim and the screen should not
   pretend otherwise. */
/* Which level of the tree to draw as phases.
 *
 * The shallowest one with enough rows to be a chart. "More than one" was the
 * rule and it broke the day the WAPA plan gained a second top level row:
 * Stabilization and Future Development sits outside the program's own WBS
 * as a sibling of the whole thing, so level one went from one row to two, and
 * a Gantt that had been drawing ten stages started drawing two bars.
 *
 * Three is the threshold. Two bars is not a schedule, it is a pair of lines.
 * If no level has three, the old rule still applies, because a plan that
 * genuinely has two phases should show two.
 */
export function pickPhaseLevel(levels) {
  const shallowest = [...levels.keys()].sort((a, b) => a - b);
  return shallowest.find((l) => levels.get(l) >= 3)
      ?? shallowest.find((l) => levels.get(l) > 1);
}

export function scheduleBars({ stages = [], deliverables = [] }) {
  const dated = stages.filter((s) => s.start_date || s.finish_date);

  if (dated.length) {
    /* A real MS Project or Primavera export is not a list of phases, it is a
       tree. WAPA's is 546 rows deep, and drawing a bar for every one of them
       would be a wall rather than a schedule. So the chart draws the
       shallowest level that has more than one row, which is the phase level in
       both tools: the ten sections of the WAPA plan rather than its 324
       leaf tasks. Everything else stays in the register underneath. */
    /* Outline level 0 is the project row itself, so `|| 1` would fold the
       root in with its children and pick them both as the phases. */
    const levelOf = (s) => {
      const n = Number(s.outline_level);
      return Number.isFinite(n) ? n : 1;
    };
    const levels = new Map();
    for (const s of dated) levels.set(levelOf(s), (levels.get(levelOf(s)) ?? 0) + 1);
    const phaseLevel = pickPhaseLevel(levels);

    const chosen = phaseLevel === undefined
      ? dated
      : dated.filter((s) => levelOf(s) === phaseLevel);

    /* A phase bar stands for everything underneath it, so it carries those
       rows. That is what lets a click on the bar open the activities it was
       drawn from instead of leaving a number nobody can act on. */
    const childrenOf = new Map();
    for (const s of stages) {
      const key = s.parent_id ?? null;
      if (!childrenOf.has(key)) childrenOf.set(key, []);
      childrenOf.get(key).push(s);
    }
    const descendants = (root) => {
      const out = [];
      const seen = new Set();
      const walk = (r) => {
        if (!r || seen.has(r.id)) return;      // an import can name a parent twice
        seen.add(r.id);
        out.push(r);
        for (const c of childrenOf.get(r.id) ?? []) walk(c);
      };
      walk(root);
      return out;
    };

    const bars = chosen
      .map((s) => ({
        id: s.id, title: s.title,
        start: s.start_date || s.finish_date,
        finish: s.finish_date || s.start_date,
        percent: num(s.percent_complete) || null,
        owner: s.owner || null,
        level: 1,
        derived: false,
        kind: "schedule_stages",
        rows: descendants(s),
      }))
      .sort((a, b) => a.start.localeCompare(b.start));

    return {
      bars, derived: false, basis: "stages",
      activityCount: dated.length, hidden: dated.length - bars.length,
    };
  }

  const datedDeliverables = deliverables
    .map((d) => ({ d, date: effectiveDueDate(d) }))
    .filter((x) => x.date);
  if (!datedDeliverables.length) return { bars: [], derived: false, basis: "none" };

  const byStage = datedDeliverables.some((x) => x.d.stage);
  const groups = new Map();
  for (const { d, date } of datedDeliverables) {
    const key = byStage ? (d.stage || "Unassigned") : monthKey(date);
    if (!groups.has(key)) {
      groups.set(key, { key, start: date, finish: date, total: 0, complete: 0, rows: [] });
    }
    const g = groups.get(key);
    if (date < g.start) g.start = date;
    if (date > g.finish) g.finish = date;
    g.total += 1;
    g.rows.push(d);
    if (isComplete(d)) g.complete += 1;
  }

  const bars = [...groups.values()]
    .sort((a, b) => a.start.localeCompare(b.start))
    .map((g) => ({
      id: g.key,
      title: byStage ? g.key : monthLabel(g.key),
      start: g.start, finish: g.finish,
      percent: g.total ? Math.round((g.complete / g.total) * 100) : null,
      count: g.total, complete: g.complete,
      level: 1, derived: true,
      kind: "deliverables", rows: g.rows,
    }));
  return { bars, derived: true, basis: byStage ? "deliverable stages" : "deliverable months" };
}

/* Everything the Gantt needs: the bars, the milestones, the month columns
   across the top, and where today falls. Positions come back as fractions of
   the window so the drawing code holds no arithmetic. */
export function scheduleModel(
  { stages = [], milestones = [], deliverables = [] } = {}, ref = today(),
) {
  const { bars, derived, basis, activityCount, hidden } =
    scheduleBars({ stages, deliverables });
  const marks = milestones
    .filter((m) => m.date)
    .map((m) => ({ title: m.title, date: m.date, status: m.status,
                   percent: num(m.percent_complete) || null, row: m }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const dates = [
    ...bars.flatMap((b) => [b.start, b.finish]),
    ...marks.map((m) => m.date),
  ].filter(Boolean).sort();

  if (!dates.length) {
    return { bars: [], milestones: [], months: [], start: null, finish: null,
             todayFraction: null, derived, basis, span: 0,
             activityCount: activityCount ?? 0, hidden: hidden ?? 0 };
  }

  /* The window runs whole months, so the columns line up with the labels and
     a bar ending on the 3rd does not leave a stub column. */
  const startKey = monthKey(dates[0]);
  const endKey = monthKey(dates[dates.length - 1]);
  const start = monthStart(startKey);
  const finish = monthEnd(endKey);

  const s = dayIndex(start);
  const span = Math.max(1, dayIndex(finish) - s);
  const at = (d) => {
    const i = dayIndex(d);
    return i === null ? null : Math.min(1, Math.max(0, (i - s) / span));
  };

  const months = [];
  for (let k = startKey; ; k = nextMonth(k)) {
    months.push({ key: k, label: monthLabel(k), left: at(monthStart(k)),
                  right: at(monthEnd(k)) });
    if (k === endKey) break;
  }

  const todayIndex = dayIndex(ref);
  const inWindow = todayIndex !== null && todayIndex >= s && todayIndex <= s + span;

  return {
    derived, basis, start, finish, span,
    activityCount: activityCount ?? bars.length, hidden: hidden ?? 0,
    months,
    todayFraction: inWindow ? at(ref) : null,
    bars: bars.map((b) => ({ ...b, left: at(b.start), right: at(b.finish) })),
    milestones: marks.map((m) => ({ ...m, at: at(m.date) })),
  };
}

/* ---------- the schedule as a tree ---------- */

/* What a row is doing, in the words a program manager uses rather than a
   scheduler's. A 546-row export means nothing to anyone until each row can be
   read at a glance, and "late" is the one that gets acted on. */
export function activityHealth(a, ref = today()) {
  const pct = num(a.percent_complete);
  if (a.finish_is_actual || pct >= 100) return "complete";
  if (a.finish_date && a.finish_date < ref) return "late";
  if (pct > 0 || a.start_is_actual) return "in progress";
  if (a.start_date && a.start_date <= ref) return "due to start";
  return "not started";
}

/* WBS numbers sort by their parts, not as text. As text 1.10 lands between
   1.1 and 1.2, which puts the tenth phase in the middle of the second. */
export function compareWbs(a, b) {
  const pa = String(a ?? "").split(".");
  const pb = String(b ?? "").split(".");
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const na = Number(pa[i]);
    const nb = Number(pb[i]);
    if (Number.isFinite(na) && Number.isFinite(nb)) {
      if (na !== nb) return na - nb;
    } else {
      const s = String(pa[i] ?? "").localeCompare(String(pb[i] ?? ""));
      if (s) return s;
    }
  }
  return 0;
}

/* The flat register turned back into the plan it came from.
 *
 * Every row already knows its parent, either by parent_id from the import or
 * by its WBS number, so nothing here guesses. What it adds is the rollup: each
 * summary row carries how many activities sit beneath it and how many of those
 * are done, late or running. That is the part that makes the tree readable by
 * someone who did not build the schedule, because a phase can then be judged
 * without opening it.
 *
 * Counts are over leaves only. A summary row is a heading, not work, and
 * counting it as well would report more activities than the plan has.
 */
export function scheduleTree(stages = [], ref = today()) {
  const nodes = new Map();
  for (const s of stages) {
    nodes.set(s.id, { ...s, children: [], depth: 0 });
  }

  const byWbs = new Map();
  for (const n of nodes.values()) if (n.activity_id) byWbs.set(n.activity_id, n);

  const roots = [];
  for (const n of nodes.values()) {
    let parent = n.parent_id ? nodes.get(n.parent_id) : null;
    if (!parent && n.activity_id && n.activity_id.includes(".")) {
      parent = byWbs.get(n.activity_id.slice(0, n.activity_id.lastIndexOf(".")));
    }
    if (parent && parent !== n) parent.children.push(n);
    else roots.push(n);
  }

  const sort = (list) => {
    list.sort((a, b) => (a.activity_id || b.activity_id
      ? compareWbs(a.activity_id, b.activity_id)
      : String(a.start_date ?? "").localeCompare(String(b.start_date ?? ""))));
    for (const n of list) sort(n.children);
  };
  sort(roots);

  const walk = (n, depth) => {
    n.depth = depth;
    n.health = activityHealth(n, ref);
    if (!n.children.length) {
      n.stats = {
        leaves: 1,
        complete: n.health === "complete" ? 1 : 0,
        late: n.health === "late" ? 1 : 0,
        running: n.health === "in progress" ? 1 : 0,
      };
      return n;
    }
    const stats = { leaves: 0, complete: 0, late: 0, running: 0 };
    for (const c of n.children) {
      walk(c, depth + 1);
      stats.leaves += c.stats.leaves;
      stats.complete += c.stats.complete;
      stats.late += c.stats.late;
      stats.running += c.stats.running;
    }
    n.stats = stats;
    /* A heading is not work, so it takes its state from what is under it. A
       phase whose own percent is blank is not "not started" when half the
       activities inside it are finished. */
    n.health =
      stats.complete === stats.leaves ? "complete"
      : stats.late ? "late"
      : stats.running || stats.complete ? "in progress"
      : n.health;
    return n;
  };
  for (const r of roots) walk(r, 0);

  return roots;
}

/* Every node flattened back into display order, with the collapsed ones left
   out. Drawing works from this so the component holds no recursion. */
export function flattenTree(roots, expanded, { match = null } = {}) {
  const out = [];
  const visit = (n) => {
    const hit = !match || match(n);
    const kids = n.children.map(visit).flat();
    if (!hit && !kids.length) return [];
    return [n, ...(expanded.has(n.id) || (match && kids.length) ? kids : [])];
  };
  for (const r of roots) out.push(...visit(r));
  return out;
}
